import { useEffect } from 'react'
import { LinkedInCallback } from 'react-linkedin-login-oauth2'
import { Route, Switch, Redirect, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import Header from '@/components/header'
import Sidebar from '@/components/sidebar'
import useSearchParams from '@/hooks/use-search-params'
import useLocalStorage from '@/hooks/use-localstorage'
import {
  ApplicationViewSelect,
  ApplicationCreate,
  ApplicationOffer,
  ApplicationContractCreate,
  ApplicationContractTypes,
  ApplicationCompletion,
  CustomContractView
} from '@/pages/Application'
import ChatContainer from '@/pages/Chat'
import CommpanyManagemmentPage from '@/pages/CompanyManagement'
import {
  ContractView,
  ContractPreview,
  CustomContractCreate,
  CustomContractSign,
  ContractManager
} from '@/pages/Contract'
import {
  MyListings,
  CreateListing,
  ListingDashboard,
  EditListing,
  VerifyListing
} from '@/pages/Listing'
import CreateListingWizard from '@/pages/Listing/CreateListingWizard'
import ErrorPage from '@/pages/Pages/ErrorPage'
import PaymentIncompleteModal from '@/features/Settings/PlanSettings/payment-incomplete-modal'
import WelcomeModal from '@/features/Profile/WelcomeModal'
import { StripePaymentContainer, StripeConnectContainer } from '@/pages/Payment'
import ProfilePage from '@/pages/Profile'
import { MyRentals, RentalDashboard } from '@/pages/Rentals'
import Settings from '@/pages/Settings'
import Support from '@/pages/Support'
import UsageCredits from '@/pages/UsageCredits'
import TrustScore from '@/pages/TrustScore'
import { MyTenants, TenantDashboard } from '@/pages/Tenants'

import OnboardingToaster from '@/components/onboarding-toaster'
import SendInBlue from '@/components/send-in-blue'
import { Layout, Wrapper, Content } from '@/components/structure'
import Callback from '@/pages/Pages/callback'
import Promotions from '@/pages/Promotions'
import useMediaQuery from '@/hooks/use-media-query'
import { breakpoints } from '@/styles'
import { useSidebarContext } from './hooks/use-sidebar-context'
import AuthenticatedView from './authenticated-view'
import { AgreeToTerms } from './agree-to-terms'

const LanguageRedirect = ({ session }) => {
  const { i18n } = useTranslation()
  const currentLanguage = i18n.languages[0]
  const userLanguage = session?.setting_language

  useEffect(() => {
    if (!userLanguage || userLanguage === currentLanguage) return
    i18n.changeLanguage(userLanguage)
  }, [userLanguage, currentLanguage, i18n])

  return null
}

const AuthenticatedApp = ({ session }) => {
  // Using max-width query will always return false on the initial
  // query while the page is loading.  To stop the loading flash we will use
  // a pointer query which is correct on the initial render.
  const { isSidebarOpen, setIsSidebarOpen } = useSidebarContext()
  const location = useLocation()
  const isCustomContract =
    location.pathname.includes('/contract/create/edit') ||
    location.pathname.includes('/custom-contract/sign')
  const isBuildingCustomContract = location.pathname.includes('/custom-contract/create')
  const isPromotionPage = location.pathname.includes('/promotions')
  const isExternalUser = !!session?.signer
  const showSidebar = !isExternalUser && !isPromotionPage
  const showHeader = !isCustomContract && !isPromotionPage
  const params = useSearchParams()
  const redirect = params.redirect
  const defaultPath = session.operation_type === 'landlord' ? '/listings' : '/profile'
  const [, setKeyboardHasBeenClosed] = useLocalStorage('keyboardHasBeenClosed')
  const isMobile = useMediaQuery(`(max-width: ${breakpoints.desktopSmall})`)

  useEffect(() => {
    setKeyboardHasBeenClosed(null)
  }, [])

  useEffect(() => {
    if (!isBuildingCustomContract) {
      return
    } else {
      setIsSidebarOpen(false)
    }
    return () => setIsSidebarOpen(true)
  }, [isBuildingCustomContract])

  return (
    <AgreeToTerms session={session}>
      <LanguageRedirect session={session} />
      <PaymentIncompleteModal />
      <WelcomeModal session={session} />
      {isMobile && <OnboardingToaster expandedSidebar={isSidebarOpen} />}
      <SendInBlue session={session} />
      <Wrapper>
        {showSidebar && <Sidebar expanded={isSidebarOpen} setSidebar={setIsSidebarOpen} />}
        <Content sidebarExpanded={isSidebarOpen} isFullWidth={!showSidebar}>
          {showHeader && <Header sidebarExpanded={isSidebarOpen} setSidebar={setIsSidebarOpen} />}
          <Layout>
            <Switch key="switch">
              <AuthenticatedView
                exact
                path="/tenants"
                component={MyTenants}
                allowTo={['landlord']}
              />
              <AuthenticatedView
                path="/tenant/:listingId"
                component={TenantDashboard}
                allowTo={['landlord']}
              />
              <AuthenticatedView exact path="/rentals" component={MyRentals} allowTo={['tenant']} />
              <AuthenticatedView
                path="/rental/:listingId"
                component={RentalDashboard}
                allowTo={['tenant']}
              />
              <Route exact path="/chat/:timelineId" component={ChatContainer} />
              <Route exact path="/chat" component={ChatContainer} />
              <AuthenticatedView
                exact
                path="/contract/:contractId/preview/:currentStep(terms)?"
                component={ContractPreview}
                allowTo={['landlord']}
              />
              <AuthenticatedView
                path="/contract/manager/:contractId"
                component={ContractManager}
                allowTo={['landlord', 'tenant']}
              />
              <Route
                path="/contract/:contractId/:step(review|terms|sign)?"
                component={ContractView}
              />
              <AuthenticatedView
                path="/listing/:listingId/contract/choose/types"
                component={ApplicationContractTypes}
                allowTo={['landlord']}
              />
              <AuthenticatedView
                path="/custom-contract/create/:contractId"
                render={props => (
                  <CustomContractCreate sidebarExpanded={isSidebarOpen} {...props} />
                )}
              />
              <AuthenticatedView
                path="/custom-contract/sign/:contractId/:supportDocId"
                component={CustomContractSign}
              />
              <AuthenticatedView
                path="/custom-contract/view/:contractId/:supportDocId"
                component={CustomContractView}
              />

              <AuthenticatedView
                path="/application/:applicationId/contract/create/:contractId(\d+)"
                component={ApplicationContractCreate}
                allowTo={['landlord']}
              />
              <AuthenticatedView
                path="/application/:applicationId/contract/create"
                component={ApplicationContractCreate}
                allowTo={['landlord']}
              />
              <AuthenticatedView
                exact
                path="/listings"
                component={MyListings}
                allowTo={['landlord']}
              />
              <AuthenticatedView
                path="/listing/:mode(create|edit)"
                component={CreateListingWizard}
                allowTo={['landlord']}
              />
              {/* TODO: REMOVE OLD CREATE/EDIT LISTING ROUTES: */}
              <AuthenticatedView
                exact
                path="/listing/edit-building/:listingId"
                component={CreateListing}
                allowTo={['landlord']}
              />
              <AuthenticatedView
                exact
                path="/listing/create-old"
                component={CreateListing}
                allowTo={['landlord']}
              />
              <AuthenticatedView
                exact
                path="/listing/:mode(create-old|edit-old)/:listingId"
                component={EditListing}
                allowTo={['landlord']}
              />
              <AuthenticatedView
                path="/listing/verify/:listingId"
                component={VerifyListing}
                allowTo={['landlord']}
              />
              <AuthenticatedView
                path="/listing/:listingId"
                component={ListingDashboard}
                allowTo={['landlord']}
              />
              <AuthenticatedView
                path="/usage-credits"
                component={UsageCredits}
                allowTo={['landlord']}
              />
              <Route
                path="/trust-score"
                render={props => <TrustScore sidebarExpanded={isSidebarOpen} {...props} />}
                allowTo={['landlord']}
              />
              <AuthenticatedView path="/promotions/:promotionId" component={Promotions} />
              <Route
                path="/settings"
                render={props => <Settings sidebarExpanded={isSidebarOpen} {...props} />}
              />
              <Route path="/support" component={Support} />
              <Route
                exact
                path="/application/:applicationId/:step(trust-score|basic-information|references|documents)?"
                component={ApplicationViewSelect}
                allowTo={['landlord']}
              />
              <Route
                exact
                path="/chat/:timelineId/application/:applicationId/:step(trust-score|basic-information|references|documents)?"
                component={ApplicationViewSelect}
                allowTo={['landlord']}
              />
              <Route
                exact
                path="/listings/:listingId/applications/:applicationId/:step(trust-score|basic-information|references|documents)?"
                allowTo={['landlord']}
                render={props => (
                  <ApplicationViewSelect sidebarExpanded={isSidebarOpen} {...props} />
                )}
              />
              <AuthenticatedView
                exact
                path="/application/create/:listingId/preview"
                component={ApplicationCreate}
                allowTo={['tenant']}
              />
              <AuthenticatedView
                exact
                path="/application/create/:listingId/complete"
                component={ApplicationCompletion}
                allowTo={['tenant']}
              />
              <AuthenticatedView
                exact
                path="/application/create/:listingId"
                component={ApplicationOffer}
                allowTo={['tenant']}
              />
              <Route exact path="/stripe-connect" component={StripeConnectContainer} />
              <Route exact path="/stripe-payment" component={StripePaymentContainer} />
              <AuthenticatedView
                path="/manage"
                component={CommpanyManagemmentPage}
                allowTo={['landlord']}
              />
              <Route path="/profile" component={ProfilePage} />
              <Route exact path="/linkedin" component={LinkedInCallback} />
              <Route path="[^/stripe]" key="404" component={ErrorPage} />
              <Route
                path="/callback"
                render={props => {
                  const { location } = props
                  const queryParams = new URLSearchParams(location.search)
                  const authToken = queryParams.get('auth_token')
                  const url = queryParams.get('url')

                  return <Callback authToken={authToken} url={url} />
                }}
              />
              <Redirect to={redirect || defaultPath} />
            </Switch>
          </Layout>
        </Content>
      </Wrapper>
    </AgreeToTerms>
  )
}

export default AuthenticatedApp
